import React from 'react';
import styles from './UserTable.module.css';
import Button from '../../common/Button/Button';
import { useNavigate } from 'react-router-dom';

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4h6v2" />
  </svg>
);

const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 1 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);

const EyeIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

// Map permissions integer (FK to access_roles) to human-readable role names
const ROLE_MAP = {
  1: 'End User - Limited',
  2: 'End User - Extended',
  3: 'Admin',
  4: 'Editor',
  12: 'Research Analyst',
  15: 'Blocked User',
};

const getRoleName = (permissionId) => {
  if (permissionId === null || permissionId === undefined) return null;
  const id = Number(permissionId);
  return ROLE_MAP[id] || `Role ${id}`;
};

// Robustly check verified status (handles boolean true/false AND integer 1/0)
const isVerified = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') return value === 'true' || value === '1';
  return false;
};

const UserTable = ({ users, onEdit, onDelete, onView, canEdit, canDelete, viewKyc }) => {
  const navigate = useNavigate();

  const handleToggleKyc = (userId) => {
    navigate('/users/kyc', { state: { userId } });
  };



  // Validate users prop
  if (!users) {
    console.error('UserTable: users prop is undefined or null');
    return (
      <div className={styles.tableContainer}>
        <div className={styles.errorState}>
          <div className={styles.errorIcon}>⚠️</div>
          <p>Error: No user data provided to table</p>
        </div>
      </div>
    );
  }

  if (!Array.isArray(users)) {
    console.error('UserTable: users prop is not an array', users);
    return (
      <div className={styles.tableContainer}>
        <div className={styles.errorState}>
          <div className={styles.errorIcon}>⚠️</div>
          <p>Error: Users data should be an array</p>
        </div>
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className={styles.tableContainer}>
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>👥</div>
          <h3>No users to display</h3>
          <p>There are no users available at the moment.</p>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Not provided';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const truncateString = (str, maxLength = 12) => {
    if (!str) return '';
    return str.length > maxLength ? `${str.substring(0, maxLength)}...` : str;
  };

  return (
    <div className={styles.tableContainer}>
      <div className={styles.tableWrapper}>
        <table className={styles.userTable}>
          <thead>
            <tr>
              <th className={styles.nameHeader}>User Info</th>

              <th className={styles.phoneHeader}>Phone</th>
              <th className={styles.phoneVerifiedHeader}>Verified</th>
              <th className={styles.emailHeader}>Email</th>
              <th className={styles.emailVerifiedHeader}>Verified</th>
              <th className={styles.roleHeader}>Role</th>
              <th className={styles.ipHeader}>Technical Info</th>
              <th className={styles.raHeader}>Res Analyst</th>
              <th className={styles.actionsHeader}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => {
              const {
                userid,
                name,
                username,
                email,
                phone,
                phoneverified,
                emailverified,
                signupip,
                signupdeviceid,
                isresearchanalyst,
                ranumber,
                ravalidity,
                isblocked,
                blockingnote,
                permissions,
              } = user;

              const isRA = parseInt(isresearchanalyst) === 1;
              const isUserBlocked = parseInt(isblocked) === 1;

              return (
                <tr
                  key={userid}
                  className={`${styles.userRow} ${isUserBlocked ? styles.blocked : ''}`}
                >
                  {/* User Info Column */}
                  <td className={styles.nameCell}>
                    <div className={styles.userInfo}>
                      <div className={styles.userName}>{name || 'Unknown User'}</div>
                      <div className={styles.userUsername}>@{username || 'N/A'}</div>
                      <div className={styles.userId}>ID: {userid}</div>
                    </div>
                  </td>



                  {/* Phone Column */}
                  <td className={styles.phoneCell}>
                    <div className={styles.contactItem}>
                      {phone || 'N/A'}

                    </div>
                  </td>

                  {/* Phone Verified Column */}
                  <td className={styles.phoneVerifiedCell}>
                    <div className={styles.contactItem}>
                      <span className={isVerified(phoneverified) ? styles.verifiedYes : styles.verifiedNo} title={isVerified(phoneverified) ? "Verified" : "Not Verified"}>
                        {isVerified(phoneverified) ? 'Yes' : 'No'}
                      </span>
                    </div>
                  </td>

                  {/* Email Column */}
                  <td className={styles.emailCell}>
                    <div className={styles.contactItem}>
                      {truncateString(email, 20) || 'N/A'}

                    </div>
                  </td>

                  {/* Email Verified Column */}
                  <td className={styles.emailVerifiedCell}>
                    <div className={styles.contactItem}>
                      <span className={isVerified(emailverified) ? styles.verifiedYes : styles.verifiedNo} title={isVerified(emailverified) ? "Verified" : "Not Verified"}>
                        {isVerified(emailverified) ? 'Yes' : 'No'}
                      </span>
                    </div>
                  </td>

                  {/* Role Column */}
                  <td className={styles.roleCell}>
                    {permissions != null && getRoleName(permissions) ? (
                      <div className={styles.permissionsList} title={getRoleName(permissions)}>
                        {getRoleName(permissions)}
                      </div>
                    ) : (
                      <span className={styles.noPermissions}>-</span>
                    )}
                  </td>

                  {/* SignUp IP */}
                  <td className={styles.ipCell}>
                    <div><span className={styles.techValue}>{signupip || 'N/A'}</span></div>
                    <div><span className={styles.techValue} title={signupdeviceid}>{truncateString(signupdeviceid, 10) || 'N/A'}</span></div>
                  </td>



                  {/* Research Analyst */}
                  <td className={styles.raCell}>
                    {isRA ? <span className={styles.raBadge}>Yes</span> : <span className={styles.notApplicable}>No</span>}
                  </td>

                  {/* Actions Column */}
                  <td className={styles.actionsCell}>
                    <div className={styles.actionButtons}>
                     {viewKyc && <button
                        onClick={() => handleToggleKyc(userid)}
                        title="View KYC Details"
                        className={styles.kycButton}
                      >
                        <EyeIcon />
                      </button>}
                      {canEdit && <button
                        onClick={() => onEdit && onEdit(user)}
                        title="Edit user"
                        className={styles.editButton}
                      >
                        <EditIcon />
                      </button>}
                      {canDelete && <button
                        onClick={() => onDelete && onDelete(user)}
                        title="Delete user"
                        className={styles.deleteButton}
                      >
                        <TrashIcon />
                      </button>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UserTable;